import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { useQuery, useQueryClient } from 'react-query';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import FilledButton from '../../components/Buttons/FilledButton';
import Pagination from '../../components/Pagination';
import EmptyFolderMessage from '../../components/Library/EmptyFolderMessage';
import DotsLoader from '../../components/DotsLoader';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import TrainingPathService from '../../services/TrainingPathService';
import UserService from '../../services/UserService';
import {
  ORG_ROLES,
  getTrainingPathStatusLabel,
  getTrainingPathStatusToken,
} from '../../constants/main';
import { useUiStore } from '../../store';
import { UiContext } from '../../context/UiContext';
import editIcon from '../../assets/images/my-meetings/edit2.svg';
import deleteIcon from '../../assets/images/my-meetings/delete.svg';

import classes from './styles.module.scss';

const ITEMS_PER_PAGE = 10;

export default function TrainingPathsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 400);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );
  const { notifyError, notifySuccess } = useContext(UiContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    setBreadcrumbsOptions([{ label: 'Training Paths', to: '/training-paths' }]);
  }, [setBreadcrumbsOptions]);

  const { data: currentUser } = useQuery('me', UserService.getMe);
  const canManageTrainingPaths =
    currentUser?.orgRole?.access === ORG_ROLES.OWNER;
  const listAcl = canManageTrainingPaths ? 'admin' : 'trainee';

  const { data, isFetching } = useQuery(
    ['training-paths', listAcl, page, debouncedSearch],
    () =>
      TrainingPathService.getTrainingPaths({
        page,
        limit: ITEMS_PER_PAGE,
        search: debouncedSearch,
        acl: listAcl,
      }),
    {
      keepPreviousData: true,
    }
  );

  const rawPaths = data?.rows || [];
  const totalCount = data?.count || rawPaths.length;
  const trainingPaths = rawPaths.map((trainingPath) => {
    if (listAcl === 'admin') {
      return trainingPath;
    }
    const derivedStatus =
      trainingPath.Assignments?.[0]?.status || trainingPath.status;
    return {
      ...trainingPath,
      status: derivedStatus,
    };
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const emptyStateCopy = useMemo(() => {
    if (debouncedSearch) {
      return "We couldn't find a training path that matches your search.";
    }

    return 'Create your first training path to curate a structured learning experience.';
  }, [debouncedSearch]);

  const handleDeleteRequest = useCallback((trainingPathId) => {
    setPendingDeleteId(trainingPathId);
    setIsDeleteModalVisible(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteId) {
      return;
    }

    try {
      await TrainingPathService.deleteTrainingPath(pendingDeleteId);
      notifySuccess('Training path deleted successfully.');
      setIsDeleteModalVisible(false);
      setPendingDeleteId(null);
      // Invalidate and refetch the list
      await queryClient.invalidateQueries(['training-paths', listAcl]);
    } catch (error) {
      console.error(error);
      notifyError(
        error?.response?.data?.message ||
          'Unable to delete training path. Please try again.'
      );
    }
  }, [pendingDeleteId, notifyError, notifySuccess, queryClient, listAcl]);

  const handleDeleteClose = useCallback(() => {
    setIsDeleteModalVisible(false);
    setPendingDeleteId(null);
  }, []);

  const pendingDeletePath = useMemo(() => {
    if (!pendingDeleteId) return null;
    return trainingPaths.find((path) => path.id === pendingDeleteId);
  }, [pendingDeleteId, trainingPaths]);

  const renderTableContent = () => {
    if (isFetching && !trainingPaths.length) {
      return (
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      );
    }

    if (!trainingPaths.length) {
      return debouncedSearch ? (
        <div className={classes.emptyState}>{emptyStateCopy}</div>
      ) : (
        <EmptyFolderMessage />
      );
    }

    return (
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Trainees</th>
            <th>Last Updated</th>
            {canManageTrainingPaths && <th />}
          </tr>
        </thead>
        <tbody>
          {trainingPaths.map((trainingPath) => {
            const assignmentsCount = trainingPath.Assignments?.length || 0;

            return (
              <tr
                key={trainingPath.id}
                onClick={() =>
                  navigate(`/training-paths/${trainingPath.id}?acl=${listAcl}`)
                }
              >
                <td className={classes.nameCell}>
                  <div className={classes.nameContent}>
                    <h3>{trainingPath.name}</h3>
                    <p>
                      {trainingPath.description?.trim()
                        ? trainingPath.description
                        : '—'}
                    </p>
                  </div>
                </td>
                <td className={classes.statusCell}>
                  <span
                    className={classes.statusBadge}
                    data-status={getTrainingPathStatusToken(trainingPath.status)}
                  >
                    {getTrainingPathStatusLabel(trainingPath.status)}
                  </span>
                </td>
                <td className={classes.traineeCell}>
                  <span className={classes.countBadge}>{assignmentsCount}</span>
                </td>
                <td className={classes.dateCell}>
                  {new Date(
                    trainingPath.updatedAt || trainingPath.createdAt
                  ).toLocaleDateString()}
                </td>
                {canManageTrainingPaths && (
                  <td className={classes.actionsCell}>
                    <div className={classes.actionsContainer}>
                      <button
                        type="button"
                        className={classes.actionButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/training-paths/${trainingPath.id}/edit`);
                        }}
                        aria-label="Edit training path"
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>
                      <button
                        type="button"
                        className={classes.actionButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteRequest(trainingPath.id);
                        }}
                        aria-label="Delete training path"
                      >
                        <img src={deleteIcon} alt="Delete" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <Layout
      hasUserBlock
      hasSearch
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      searchPlaceholder="Search training paths..."
    >
      <div className={classes.TrainingPathsPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
              <Breadcrumbs
                breadcrumbsOptions={[
                  { label: 'Training Paths', to: '/training-paths' },
                ]}
              />
              <p className={classes.subtitle}>
                Use folders and videos to create custom learning plans for your team
              </p>
            </div>

            {canManageTrainingPaths && (
            <div className={classes.actionsRow}>
              <FilledButton onClick={() => navigate('/training-paths/trainees')}>
                  Manage Trainees
              </FilledButton>
                <FilledButton
                  onClick={() => navigate('/create-training-path')}
                  data-testid="create-training-path-btn"
                >
                  Create Training Path
                </FilledButton>
              </div>
            )}

          <div className={classes.tableContainer}>{renderTableContent()}</div>

          {totalCount > ITEMS_PER_PAGE && (
            <div className={classes.pagination}>
              <Pagination
                activePage={page}
                itemsCountPerPage={ITEMS_PER_PAGE}
                totalItemsCount={totalCount}
                pageRangeDisplayed={5}
                onChange={(nextPage) => setPage(nextPage)}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmActionModal
        title="Delete Training Path?"
        message={
          pendingDeletePath
            ? `This will permanently delete "${pendingDeletePath.name}" and all associated data including trainee assignments and progress. This cannot be undone.`
            : 'This will permanently delete the training path and all associated data. This cannot be undone.'
        }
        show={isDeleteModalVisible}
        handleClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Layout>
  );
}

